/**
 * Loads and caches the current user's permission assignments so shared permission hooks
 * and access-controlled UI can consume one stable authorization state.
 */
import { useMemo } from 'react';
import { useMyPermissionsControllerGetMyPermissionsQuery } from 'src/store/leela-api';
import type { PermissionAssignmentDto } from 'src/store/generated/leela-api-generated';

export interface MyPermissions {
  readonly isPlatformAdmin: boolean;
  readonly isBusinessUnitAdmin: boolean;
  readonly businessUnitAdminOf: ReadonlyArray<{ id: string; name: string; countries: string[] }>;
  readonly assignments: readonly PermissionAssignmentDto[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

export interface UseMyPermissionsOptions {
  readonly skip?: boolean;
}

export function useMyPermissions (options: UseMyPermissionsOptions = {}): MyPermissions {
  const { data, isLoading, isError } = useMyPermissionsControllerGetMyPermissionsQuery(
    undefined,
    options,
  );

  const result = useMemo((): Omit<MyPermissions, 'isLoading' | 'isError'> => {
    if (!data) {
      return {
        isPlatformAdmin: false,
        isBusinessUnitAdmin: false,
        businessUnitAdminOf: [],
        assignments: [],
      };
    }

    const { isPlatformAdmin, businessUnitAdminOf, assignments } = data;

    return {
      isPlatformAdmin,
      isBusinessUnitAdmin: businessUnitAdminOf.length > 0,
      businessUnitAdminOf,
      assignments,
    };
  }, [data]);

  return { ...result, isLoading, isError };
}
